import { updateSessionById } from '@/pages/api/scribeAPI';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type SessionResponse } from '@heidi/session/contracts';
import { signalWithTimeout } from '@heidi/session/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const UPDATE_SESSION_RECORDING_STATUS = 'UpdateSessionRecordingStatus';

/**
 * Datadog facet for a failed status write, so the volume this gate opens up is
 * countable after ship: how often an offline write actually fails, and how often
 * it is the deadline rather than the network that ends it.
 */
const TAG_WRITE_FAILED = 'tag="recording_status_write_failed"';

/**
 * Request deadline. Deliberately the session GET's 30s number rather than a
 * budget derived for a foreground click — a stop is awaited, so a tighter bound
 * would fail sooner inside the clinician's patience. It exists at all because
 * the shared Axios instance is created with no `timeout`, so a stalled socket
 * would otherwise leave this pending forever.
 *
 * **The number is borrowed from the GET; the GET's kill switch is not.**
 * `useQuerySessionById` gates its 30s behind
 * `release-scribe-session-fetch-timeout`. This one is ungated, and the reason is
 * narrower than "a failed write is invisible" — it is not invisible. A rejection
 * propagates to the awaiting callers: `SessionRecordingOverlay` clears
 * `boundSessionId` only *after* its `await`, so a rejected stop leaves the dead
 * session bound and acks the agent command failed; and
 * `useMutateUploadBackupRecording` awaits the same IDLE write precisely so a
 * later refetch cannot restore `RECORDING`.
 *
 * What makes the flag unnecessary is the direction of the change, not the size
 * of its blast radius. This replaces an **unbounded** wait, and every one of
 * those consequences was already reachable — and worse — when the request simply
 * hung and the caller never settled at all. There is no new branch for a flag to
 * switch off; reverting the constant is the rollback.
 *
 * That argument does depend on 30s being long enough that only a genuinely
 * stalled socket reaches it. Shortening it materially is a different decision,
 * because it would start aborting requests that would have succeeded — and that
 * one would want the GET's gate.
 */
const RECORDING_STATUS_REQUEST_TIMEOUT_MS = 30_000;

export function useMutateUpdateSessionRecordingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_SESSION_RECORDING_STATUS],
    // The gate is deliberately NOT platform-scoped, and the trade is real: web
    // loses deferred retry, because the two sibling stop mutations are also
    // unconditional and web-reachable. Scoping only this third write would split
    // one click across two gates — the stop failing fast while its status
    // companion defers, leaving the backend in a state neither write agrees on.
    //
    // A failed write leaves the optimistic idle/paused cache entry disagreeing
    // with the server's stale RECORDING, which a later refetch can restore. That
    // divergence is left for the offline sync path to reconcile rather than
    // rolled back here: rolling back to RECORDING while native capture is
    // already torn down would assert something even less true.
    networkMode: 'offlineFirst',
    mutationFn: async (input: {
      recordingStatus: RecordingStatus;
      sessionId: string;
      onMutate?: () => void;
      onSuccess?: () => void;
    }) => {
      const sessionId = input.sessionId;

      if (input.recordingStatus === RecordingStatus.RECORDING) {
        return { success: true };
      }

      return await updateSessionById(
        sessionId,
        { recording_status: input.recordingStatus },
        signalWithTimeout(undefined, RECORDING_STATUS_REQUEST_TIMEOUT_MS)
      );
    },
    onMutate: (input: {
      recordingStatus: RecordingStatus;
      sessionId: string;
      onMutate?: () => void;
    }) => {
      queryClient.setQueryData<SessionResponse>(
        [GET_SESSION_BY_ID, { sessionId: input.sessionId }],
        (old) => {
          if (!old) return undefined;

          return {
            ...old,
            recording_status: input.recordingStatus,
          };
        }
      );

      if (input.onMutate) {
        input.onMutate();
      }
    },
    onError: (error, input) => {
      // Tags and enums only. The gate above turns this from a rare 4xx/5xx into
      // the normal outcome of every offline stop, and the Datadog transport does
      // not apply pino's redact list, so a raw error here would export the
      // request's bearer header and URL. `sanitizeError` keeps `status` and
      // `code`, which is also what separates ERR_NETWORK (offline) from
      // ERR_CANCELED (the deadline below) once this is countable in Datadog.
      //
      // ERR_CANCELED, not ECONNABORTED: the request is ended by the `signal`,
      // and axios's abort listener rejects with a `CanceledError` whenever it is
      // handed an event with a `type` — which an `abort` Event always has.
      // ECONNABORTED belongs to axios's own `timeout` option, which this
      // instance does not set. That absence is why the deadline exists at all.
      logger.warn(`Failed to update session recording status ${TAG_WRITE_FAILED}`, {
        sessionId: input.sessionId,
        ...sanitizeError(error),
      });
    },
    onSuccess: (_, input) => {
      if (input.onSuccess) {
        input.onSuccess();
      }
    },
  });
}
